/**
 * Facilities Data
 * Comprehensive facility data with Aged Care and In-House facilities only
 */

import { createFacility, AGED_CARE_TYPES, BUSINESS_GROUP_STRUCTURES } from '../data/models/Facility';

// Business Groups
export const BUSINESS_GROUPS = {
  'medfast_group': {
    id: 'medfast_group',
    name: 'MedFast Healthcare Group',
    abn: '12345678901',
    corporateStructure: BUSINESS_GROUP_STRUCTURES.GROUP,
    parentCompany: null,
    facilities: ['sunshine', 'bayview', 'greenhill', 'rosewood', 'community']
  }
};

// Facilities Data
export const facilitiesData = [
  // Aged Care Facilities - MedFast Group
  createFacility({
    id: 'sunshine',
    name: 'Sunshine Gardens Aged Care',
    legalName: 'Sunshine Gardens Aged Care Pty Ltd',
    abn: '12345678901',
    address: {
      street: '123 Care Street',
      city: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      country: 'Australia',
      fullAddress: '123 Care Street, Melbourne VIC 3000'
    },
    phone: '(03) 9123 4567',
    email: 'sunshine@medfastcrm.com.au',
    website: 'https://sunshine.medfast.com.au',
    manager: 'Sarah Mitchell',
    managerId: 2,
    facilityTypes: ['agedcare'],
    primaryType: 'agedcare',
    agedCare: {
      enabled: true,
      providerNumber: 'AC123456',
      agedCareType: AGED_CARE_TYPES.RESIDENTIAL,
      accreditationNumber: 'ACC-2024-001',
      accreditationExpiry: '2025-12-31',
      beds: 120,
      occupancy: 94,
      careLevels: ['low', 'medium', 'high', 'dementia', 'palliative'],
      anaccEnabled: true,
      enrmcEnabled: true,
      supportAtHomeEnabled: true
    },
    ndis: {
      enabled: false
    },
    businessGroup: {
      id: 'medfast_group',
      name: 'MedFast Healthcare Group',
      abn: '12345678901',
      corporateStructure: BUSINESS_GROUP_STRUCTURES.GROUP
    },
    totalWards: 4,
    totalResidents: 89,
    totalStaff: 45,
    rating: 4.8,
    accreditationStatus: 'active',
    fundingSource: ['aged_care', 'private'],
    systemContext: 'agedcare',
    status: 'active'
  }),

  createFacility({
    id: 'bayview',
    name: 'Bayview Gardens Aged Care',
    legalName: 'Bayview Gardens Aged Care Pty Ltd',
    abn: '12345678901',
    address: {
      street: '456 Ocean Road',
      city: 'St Kilda',
      state: 'VIC',
      postcode: '3182',
      country: 'Australia',
      fullAddress: '456 Ocean Road, St Kilda VIC 3182'
    },
    phone: '(03) 9876 5432',
    email: 'bayview@medfastcrm.com.au',
    manager: 'Michael Chen',
    facilityTypes: ['agedcare'],
    primaryType: 'agedcare',
    agedCare: {
      enabled: true,
      providerNumber: 'AC123457',
      agedCareType: AGED_CARE_TYPES.RESIDENTIAL,
      beds: 85,
      occupancy: 91,
      careLevels: ['low', 'medium', 'high'],
      anaccEnabled: true,
      enrmcEnabled: true,
      supportAtHomeEnabled: false
    },
    businessGroup: {
      id: 'medfast_group',
      name: 'MedFast Healthcare Group',
      abn: '12345678901'
    },
    totalWards: 3,
    totalResidents: 56,
    totalStaff: 32,
    rating: 4.7,
    systemContext: 'agedcare',
    status: 'active'
  }),

  createFacility({
    id: 'greenhill',
    name: 'Green Hill Residence',
    address: {
      street: '789 Park Avenue',
      city: 'Brighton',
      state: 'VIC',
      postcode: '3186',
      country: 'Australia',
      fullAddress: '789 Park Avenue, Brighton VIC 3186'
    },
    phone: '(03) 9555 1234',
    email: 'greenhill@medfastcrm.com.au',
    manager: 'David Wilson',
    facilityTypes: ['agedcare'],
    primaryType: 'agedcare',
    agedCare: {
      enabled: true,
      providerNumber: 'AC123458',
      agedCareType: AGED_CARE_TYPES.RESIDENTIAL,
      beds: 60,
      occupancy: 88,
      careLevels: ['low', 'medium'],
      anaccEnabled: true,
      enrmcEnabled: true,
      supportAtHomeEnabled: false
    },
    businessGroup: {
      id: 'medfast_group',
      name: 'MedFast Healthcare Group',
      abn: '12345678901'
    },
    totalWards: 2,
    totalResidents: 42,
    totalStaff: 25,
    rating: 4.9,
    systemContext: 'agedcare',
    status: 'active'
  }),

  createFacility({
    id: 'rosewood',
    name: 'Rosewood Care Community',
    address: {
      street: '321 Garden Lane',
      city: 'Hawthorn',
      state: 'VIC',
      postcode: '3122',
      country: 'Australia',
      fullAddress: '321 Garden Lane, Hawthorn VIC 3122'
    },
    phone: '(03) 9444 7890',
    email: 'rosewood@medfastcrm.com.au',
    manager: 'Emma Thompson',
    facilityTypes: ['agedcare'],
    primaryType: 'agedcare',
    agedCare: {
      enabled: true,
      providerNumber: 'AC123459',
      agedCareType: AGED_CARE_TYPES.RESIDENTIAL,
      beds: 100,
      occupancy: 92,
      careLevels: ['low', 'medium', 'high', 'dementia'],
      anaccEnabled: true,
      enrmcEnabled: true,
      supportAtHomeEnabled: true
    },
    businessGroup: {
      id: 'medfast_group',
      name: 'MedFast Healthcare Group',
      abn: '12345678901'
    },
    totalWards: 4,
    totalResidents: 78,
    totalStaff: 42,
    rating: 4.6,
    systemContext: 'agedcare',
    status: 'active'
  }),

  // In-House / Home Care Facility - MedFast Group
  createFacility({
    id: 'community',
    name: 'Community Care Services',
    address: {
      street: '100 Care Boulevard',
      city: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      country: 'Australia',
      fullAddress: '100 Care Boulevard, Melbourne VIC 3000'
    },
    phone: '(03) 9000 0000',
    email: 'community@medfastcrm.com.au',
    manager: 'John Smith',
    facilityTypes: ['inhouse'],
    primaryType: 'inhouse',
    inHouse: {
      enabled: true,
      providerNumber: 'IH123456',
      supportedClients: 25,
      serviceAreas: ['Melbourne', 'St Kilda', 'Brighton']
    },
    businessGroup: {
      id: 'medfast_group',
      name: 'MedFast Healthcare Group',
      abn: '12345678901'
    },
    totalWards: 3,
    totalResidents: 35,
    totalStaff: 30,
    rating: 4.7,
    fundingSource: ['in_house'],
    systemContext: 'agedcare',
    status: 'active'
  })
];

export default facilitiesData;

